/**
 * Platform Field Limits & Validation
 *
 * SINGLE SOURCE OF TRUTH for all platform-specific field constraints.
 * To update limits when a platform changes requirements, edit ONLY this file.
 *
 * References:
 * - Meta: https://developers.facebook.com/docs/marketing-api/creative/
 * - Google Ads: https://support.google.com/google-ads/answer/1704395
 * - TikTok: https://ads.tiktok.com/help/article/ad-specs-placements
 * - Pinterest: https://help.pinterest.com/en/business/article/pinterest-ad-specs
 * - Twitter/X: https://developer.twitter.com/en/docs/twitter-ads-api
 */

export interface PlatformFieldLimits {
  headline?: number;
  description?: number;
  body?: number;
  caption?: number;
  cta_text?: number;
  link_url?: number;
  display_url?: number;
  keyword?: number;
  /** max keywords per ad group */
  keywords_per_group?: number;
  /** max ad variations per ad set / ad group */
  variations_per_adset?: number;
}

/** Complete set of character/count limits per platform */
export const PLATFORM_LIMITS: Record<string, PlatformFieldLimits> = {
  meta: {
    headline: 40,
    description: 125,
    body: 125,
    cta_text: 25,
    link_url: 2048,
  },
  google_ads: {
    headline: 30,
    description: 90,
    cta_text: 15,
    display_url: 15, // each path component
    link_url: 2048,
    keyword: 80,
    keywords_per_group: 20,
    variations_per_adset: 3, // Google RSA uses 15 headlines, but individual ad objects ≤ 3
  },
  tiktok: {
    caption: 100,
    headline: 100,
    cta_text: 25,
    link_url: 2048,
  },
  pinterest: {
    headline: 100,
    description: 500,
    caption: 500,
    link_url: 2048,
  },
  twitter: {
    headline: 70,
    body: 280, // tweet text
    description: 200,
    cta_text: 20,
    link_url: 2048,
  },
};

/** Human-readable field names for violation messages */
const FIELD_LABELS: Record<string, string> = {
  headline: 'Headline',
  description: 'Description',
  body: 'Body / Tweet text',
  caption: 'Caption',
  cta_text: 'CTA button text',
  link_url: 'Link URL',
  display_url: 'Display URL path',
  keyword: 'Keyword',
  keywords_per_group: 'Keywords per ad group',
  variations_per_adset: 'Ad variations per set',
};

export interface FieldViolation {
  path: string;           // e.g. "ad_sets[0].ads[1].headline"
  field: string;          // field name
  label: string;          // human label
  platform: string;
  value: string | number; // the actual value or count
  limit: number;
  /** 'length' for string over-length, 'count' for too many items */
  type: 'length' | 'count';
}

/** Clamp a string to test length ignoring undefined/null */
function strLen(v: unknown): number | null {
  if (typeof v === 'string') return v.length;
  return null;
}

/**
 * Validate a single ad variation object against platform limits.
 * Returns any violations found.
 */
function validateVariation(
  variation: Record<string, any>,
  platform: string,
  basePath: string,
  limits: PlatformFieldLimits,
): FieldViolation[] {
  const violations: FieldViolation[] = [];
  const stringFields: Array<keyof PlatformFieldLimits> = [
    'headline', 'description', 'body', 'caption', 'cta_text', 'link_url', 'display_url',
  ];

  for (const field of stringFields) {
    const limit = limits[field];
    if (limit == null) continue;

    const raw = variation[field];
    // Some fields may be arrays (e.g. Google headlines array) — check each element
    const values: unknown[] = Array.isArray(raw) ? raw : [raw];
    values.forEach((v, idx) => {
      const len = strLen(v);
      if (len !== null && len > limit) {
        const path = Array.isArray(raw)
          ? `${basePath}.${field}[${idx}]`
          : `${basePath}.${field}`;
        violations.push({
          path,
          field,
          label: FIELD_LABELS[field] || field,
          platform,
          value: v as string,
          limit,
          type: 'length',
        });
      }
    });
  }

  // Keyword count check
  if (limits.keyword != null && Array.isArray(variation.keywords)) {
    variation.keywords.forEach((kw: any, idx: number) => {
      const text = typeof kw === 'object' ? kw?.text : String(kw ?? '');
      const len = strLen(text);
      if (len !== null && len > limits.keyword!) {
        violations.push({
          path: `${basePath}.keywords[${idx}]`,
          field: 'keyword',
          label: FIELD_LABELS.keyword,
          platform,
          value: text,
          limit: limits.keyword!,
          type: 'length',
        });
      }
    });

    if (limits.keywords_per_group != null && variation.keywords.length > limits.keywords_per_group) {
      violations.push({
        path: `${basePath}.keywords`,
        field: 'keywords_per_group',
        label: FIELD_LABELS.keywords_per_group,
        platform,
        value: variation.keywords.length,
        limit: limits.keywords_per_group,
        type: 'count',
      });
    }
  }

  return violations;
}

/**
 * Validate an entire campaign proposal object.
 *
 * Handles both top-level `platform` on the proposal and per-ad-set platform overrides.
 * Works with the canonical proposal format:
 *   { platform, ad_sets: [{ ads: [{ headline, description, ... }] }] }
 *
 * Returns an array of FieldViolation. Empty array = no issues.
 */
export function validateProposal(proposal: Record<string, any>): FieldViolation[] {
  if (!proposal || typeof proposal !== 'object') return [];

  const violations: FieldViolation[] = [];
  const topPlatform = (proposal.platform || '').toLowerCase();

  // Handle wrapper format { proposal: {...}, resolved_media: [...] }
  const root = proposal.proposal ?? proposal;
  const adSets: any[] = root.ad_sets ?? root.ad_groups ?? root.campaigns ?? [];

  adSets.forEach((adSet: any, setIdx: number) => {
    const setPath = `ad_sets[${setIdx}]`;
    const setLabel = adSet.name || adSet.ad_set_name || `Ad Set ${setIdx + 1}`;
    const platform = (adSet.platform || topPlatform || '').toLowerCase();
    const limits = PLATFORM_LIMITS[platform];

    if (!limits) return; // unknown platform — skip

    // Check variations_per_adset
    const ads: any[] = adSet.ads ?? adSet.variations ?? adSet.ad_creatives ?? [];
    if (limits.variations_per_adset != null && ads.length > limits.variations_per_adset) {
      violations.push({
        path: `${setPath}.ads`,
        field: 'variations_per_adset',
        label: `Ad variations in "${setLabel}"`,
        platform,
        value: ads.length,
        limit: limits.variations_per_adset,
        type: 'count',
      });
    }

    ads.forEach((ad: any, adIdx: number) => {
      const adPath = `${setPath}.ads[${adIdx}]`;
      const adViolations = validateVariation(ad, platform, adPath, limits);
      violations.push(...adViolations);
    });
  });

  return violations;
}

/** Group violations by ad set path for easier rendering */
export function groupViolationsByAdSet(violations: FieldViolation[]): Map<string, FieldViolation[]> {
  const map = new Map<string, FieldViolation[]>();
  for (const v of violations) {
    // Extract "ad_sets[N]" prefix
    const match = v.path.match(/^(ad_sets\[\d+\])/);
    const key = match ? match[1] : 'general';
    const list = map.get(key) ?? [];
    list.push(v);
    map.set(key, list);
  }
  return map;
}
